import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FaLock, FaCheckCircle } from 'react-icons/fa';
import './Login.css';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`/auth/verify-reset-token/${token}`);

      if (response.data.success) {
        setTokenValid(true);
        setUserInfo(response.data.data);
      }
    } catch (err) {
      console.error('Token verification error:', err);
      setError(err.response?.data?.message || 'Invalid or expired reset link');
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/auth/reset-password', {
        token,
        newPassword: formData.newPassword
      });

      if (response.data.success) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="login-container">
        <div className="login-background">
          <div className="login-pattern"></div>
        </div>
        <div className="login-card">
          <div className="loading-message">
            <span className="spinner"></span>
            <p>Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="login-container">
        <div className="login-background">
          <div className="login-pattern"></div>
        </div>

        <div className="login-card">
          <div className="login-header">
            <div className="logo-container">
              <img
                src="/logo-full.png"
                alt="Bad Boy Bail Bonds"
                className="logo-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="logo-text-fallback" style={{ display: 'none' }}>
                <h1 className="company-name">BAD BOY</h1>
                <h2 className="company-type">BAIL BONDS</h2>
              </div>
            </div>
            <p className="login-subtitle">Invalid Reset Link</p>
          </div>

          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            {error}
          </div>

          <p className="help-text" style={{ textAlign: 'center', margin: '1.5rem 0' }}>
            This password reset link is invalid or has expired. Please request a new one.
          </p>

          <Link to="/forgot-password" className="btn btn-primary btn-block">
            Request New Reset Link
          </Link>

          <div className="login-links">
            <Link to="/login" className="link-secondary">
              Back to Login
            </Link>
          </div>

          <div className="login-footer">
            <p className="copyright">
              © 2025 Bad Boy Bail Bonds - Montana Pretrial Services LLC
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="login-container">
        <div className="login-background">
          <div className="login-pattern"></div>
        </div>

        <div className="login-card">
          <div className="login-header">
            <div className="logo-container">
              <img
                src="/logo-full.png"
                alt="Bad Boy Bail Bonds"
                className="logo-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="logo-text-fallback" style={{ display: 'none' }}>
                <h1 className="company-name">BAD BOY</h1>
                <h2 className="company-type">BAIL BONDS</h2>
              </div>
            </div>
            <p className="login-subtitle">Password Reset Successful</p>
          </div>

          <div className="success-message">
            <div className="success-icon"><FaCheckCircle /></div>
            <h3>Password Changed!</h3>
            <p>Your password has been successfully reset.</p>
            <p className="help-text">You will be redirected to the login page in a few seconds...</p>
          </div>

          <Link to="/login" className="btn btn-primary btn-block">
            Go to Login
          </Link>

          <div className="login-footer">
            <p className="copyright">
              © 2025 Bad Boy Bail Bonds - Montana Pretrial Services LLC
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="login-pattern"></div>
      </div>

      <div className="login-card">
        <div className="login-header">
          <div className="logo-container">
            <img
              src="/logo-full.png"
              alt="Bad Boy Bail Bonds"
              className="logo-image"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div className="logo-text-fallback" style={{ display: 'none' }}>
              <h1 className="company-name">BAD BOY</h1>
              <h2 className="company-type">BAIL BONDS</h2>
            </div>
          </div>
          <p className="login-subtitle">Reset Your Password</p>
        </div>

        {userInfo && (
          <p className="help-text" style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
            Hello <strong>{userInfo.firstName}</strong>, please enter your new password below.
          </p>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <div className="input-with-icon">
              <FaLock className="input-icon" />
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter new password (min. 8 characters)"
                required
                minLength="8"
                autoFocus
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div className="input-with-icon">
              <FaLock className="input-icon" />
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
                required
                minLength="8"
                disabled={loading}
              />
            </div>
          </div>

          <div className="password-requirements">
            <p style={{ fontSize: '0.85rem', color: '#666', margin: '0.5rem 0' }}>
              Password requirements:
            </p>
            <ul style={{ fontSize: '0.8rem', color: '#666', paddingLeft: '1.5rem', margin: 0 }}>
              <li>At least 8 characters long</li>
              <li>Passwords must match</li>
            </ul>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-login"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-small"></span>
                Resetting Password...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>

        <div className="login-links">
          <Link to="/login" className="link-secondary">
            Back to Login
          </Link>
        </div>

        <div className="login-footer">
          <p className="copyright">
            © 2025 Bad Boy Bail Bonds - Montana Pretrial Services LLC
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
